/**
 * 74.13 (ARCHITECTURE §5.47; the coordinator, Oct 30: the seam closed before the Landforms row goes on by default): the
 * landforms' ground records drawn at any distance. A cliff band or a crest (`PlanetState.shapings`, core/landforms.ts) lies
 * between the mesh's vertices, so the planet's mesh draws the soft coarse face under it, and the fine patch, which draws the
 * person's ground (the records with it), fades back to the mesh between 29 and 53 people (FINE_PATCH): a band held 18–27
 * people over the mesh sank into it as it receded, while the sim's sight still met it.
 *
 * Here each record gets a strip of its own over its footprint: the ground it makes of the mesh (core/shaping.ts), a hair
 * under it, so the patch, which adds the relief over it, covers it near; the mesh's colour turning to rock as it rises, flat
 * shaded as the mesh is, and lifted by the mesh's own far lift and sink (applyGroundLift), a child of the planet's mesh. Its
 * grid lies on the record's creases — rows on the square ends and down each cap to the face's foot, columns on the shelf's
 * edge and at the face's foot — so the flat triangles follow them. Where records overlap, each strip is drawn whole and the
 * depth buffer keeps the highest, as the sim's ground is the highest of them (and the mesh keeps the highest where none
 * lifts it). Built once with the mesh; a crater after does not rebuild it (the patch draws the near ground as it is).
 * Without records, nothing. Presentation only.
 */
import * as THREE from 'three';
import type { PlanetState, Shaping } from '../types';
import { getSurfaceRadius, nearestSurfaceVertex } from '../core/surface';
import { shapedAt } from '../core/shaping';
import { layerProfileFor, hexToRgb } from '../core/planetColors';
import { PERSON_BODY_HEIGHT, SCALE_PROFILES } from '../core/scaleProfile';

/** A person at the human scale, the landforms' only one (DECISIONS 374 (7)). */
const HUMAN_PERSON = PERSON_BODY_HEIGHT * SCALE_PROFILES.human.kHuman;

/**
 * The strips' dials, in people unless said: the samples' spacing across a shelf and down a cap, seen past the patch's 53
 * people, and along a record's body in those steps (thalia's megaplanet 30k triangles, its planet mesh 20k); how far under
 * the record's ground a strip lies (the patch's relief is ≥ 0 over it); how far under the mesh a point the record does not
 * lift lies; the least lift a strip draws (under it the mesh does: drawn, it peeked through as slivers); and the lift at
 * which a strip is all rock (under it, the mesh's own colour where it stands, toward the rock).
 */
export const LANDFORM_FAR = { stepPeople: 5, bodySteps: 2, underPeople: 0.05, tuckPeople: 0.3, drawnPeople: 0.3, rockPeople: 6 };

/** The records' strips, sampled: positions (relative to the planet's centre) and the triangles that show (any corner lifted). */
export interface LandformStrips { positions: Float32Array; colors: Float32Array; index: Uint32Array; triangles: number }

const norm = (x: number, y: number, z: number): [number, number, number] => { const l = Math.sqrt(x * x + y * y + z * z) || 1; return [x / l, y / l, z / l]; };

/** The strips of a planet's records (null without any): pure — the planet's ground and its records alone. */
export function landformStrips(planet: PlanetState, person = HUMAN_PERSON, meshColors?: Float32Array): LandformStrips | null {
  const list = planet.shapings;
  if (!list?.length) return null;
  const R = planet.radius;
  const step = LANDFORM_FAR.stepPeople * person, under = LANDFORM_FAR.underPeople * person, tuck = LANDFORM_FAR.tuckPeople * person;
  const raisedEps = LANDFORM_FAR.drawnPeople * person;
  const rock = hexToRgb(layerProfileFor(planet).rock);
  const pos: number[] = [], col: number[] = [], idx: number[] = [];
  const strip = (s: Shaping): void => {
    if (s.footprint.kind !== 'ribbon') return;
    const nodes = s.footprint.nodes, half = s.footprint.half, E = half + s.reach;
    for (let k = 0; k + 1 < nodes.length; k++) {
      const [ax, ay, az] = norm(nodes[k].dir.x, nodes[k].dir.y, nodes[k].dir.z), [bx, by, bz] = norm(nodes[k + 1].dir.x, nodes[k + 1].dir.y, nodes[k + 1].dir.z);
      const ab = ax * bx + ay * by + az * bz;
      const [tx, ty, tz] = norm(bx - ax * ab, by - ay * ab, bz - az * ab);   // along the spine, at a
      const [nx, ny, nz] = norm(ay * tz - az * ty, az * tx - ax * tz, ax * ty - ay * tx);   // across it
      const L = Math.acos(Math.max(-1, Math.min(1, ab))) * R;
      const at = (sA: number, x: number) => norm(ax + (tx * sA + nx * x) / R, ay + (ty * sA + ny * x) / R, az + (tz * sA + nz * x) / R);
      // this record's own lift over the mesh at a place (sA along from a, x across)
      const rise = (sA: number, x: number): number => {
        const [ux, uy, uz] = at(sA, x), g = getSurfaceRadius(planet, { x: ux, y: uy, z: uz });
        return shapedAt(s, R, ux, uy, uz, g) - g;
      };
      // how far out the record still lifts the ground from (sA, x) stepping (dS, dX) per unit: scanned at the step, then
      // bisected to the face's foot (0: not at all) — the grid's last row or column on the crease, not cutting across it
      const reachOut = (sA: number, x: number, dS: number, dX: number, most: number): number => {
        let last = -1;
        for (let t = 0; t <= most + 1e-9; t += step) if (rise(sA + dS * t, x + dX * t) > raisedEps) last = t;
        if (last < 0) return 0;
        let lo = last, hi = Math.min(most, last + step);
        for (let it = 0; it < 8; it++) { const m = (lo + hi) / 2; if (rise(sA + dS * m, x + dX * m) > raisedEps) lo = m; else hi = m; }
        return hi;
      };
      // rows: down each cap only as far as the face runs past the end (its square end's edge a row), the body at up to
      // bodySteps steps (a uniform grid cut the caps' faces into spikes)
      const capOut = (sA: number, dS: number) => Math.max(...[-half, -half / 2, half / 2, half].map(x => reachOut(sA, x, dS, 0, s.reach)));
      const c0 = capOut(0, -1), c1 = capOut(L, 1);
      const rows: number[] = [];
      const run = (from: number, to: number, n: number) => { for (let a = 0; a < n; a++) rows.push(from + (to - from) * a / n); };
      if (c0 > 0) run(-c0, 0, Math.ceil(c0 / step));
      run(0, L, Math.max(1, Math.ceil(L / (LANDFORM_FAR.bodySteps * step))));
      if (c1 > 0) run(L, L + c1, Math.ceil(c1 / step));
      rows.push(L + c1);
      // columns: the shelf spread over its width (its edge, ±half, a column) and the face's foot on each side, so the flat
      // triangles follow the shelf's edge and the face's foot rather than cutting their corners
      const ns = Math.max(1, Math.ceil(half / step)), nt = 2 * ns + 3;
      const base = pos.length / 3, lifted = new Uint8Array(rows.length * nt), xs = new Float64Array(rows.length * nt);
      for (let a = 0; a < rows.length; a++) {
        const sA = rows[a], out = [-1, 1].map(sd => reachOut(sA, 0, 0, sd, E));
        for (let b = 0; b < nt; b++) {
          const j = b - (ns + 1), sd = j < 0 ? 0 : 1, m = Math.abs(j), ext = out[sd], shelf = Math.min(half, ext);
          const x = (j < 0 ? -1 : 1) * (m === 0 ? 0 : m <= ns ? shelf * m / ns : ext);
          xs[a * nt + b] = x;
          const [ux, uy, uz] = at(sA, x);
          const coarse = getSurfaceRadius(planet, { x: ux, y: uy, z: uz });
          // the record's own ground, a hair under it, where it lifts the mesh; under the mesh where not. Where records overlap
          // each strip is drawn whole and the depth buffer keeps the highest, as the sim's ground is the highest of them — clipped
          // to where each held it highest, a flat triangle cut across the line where one record's face met another's shelf
          const own = shapedAt(s, R, ux, uy, uz, coarse), mine = own - coarse > raisedEps;
          const r = mine ? own - under : coarse - tuck;
          lifted[a * nt + b] = mine ? 1 : 0;
          pos.push(ux * r, uy * r, uz * r);
          // the mesh's own colour where it stands, toward the rock the higher the record holds it (a face is rock; a shelf's lip the ground's)
          const lift = Math.max(0, r - coarse), w = Math.min(1, lift / (LANDFORM_FAR.rockPeople * person));
          const v = meshColors ? nearestSurfaceVertex(planet, { x: ux, y: uy, z: uz }) : -1;
          const g0 = v >= 0 ? [meshColors![3 * v], meshColors![3 * v + 1], meshColors![3 * v + 2]] : [rock.r, rock.g, rock.b];
          col.push(g0[0] + (rock.r - g0[0]) * w, g0[1] + (rock.g - g0[1]) * w, g0[2] + (rock.b - g0[2]) * w);
        }
      }
      // (a side the record does not lift folds its columns onto the spine: those triangles have no area and are left out)
      // (74.15: wound outward — counter-clockwise seen from above — as the planet's mesh is: wound inward, every one was culled
      // from outside and the far side's faces showed through the near, shaded for the far side: the black lids along the rims)
      for (let a = 0; a + 1 < rows.length; a++) for (let b = 0; b + 1 < nt; b++) {
        const p = a * nt + b, q = p + 1, r = p + nt, w = r + 1;
        if ((lifted[p] || lifted[q] || lifted[r]) && xs[p] !== xs[q]) idx.push(base + p, base + r, base + q);
        if ((lifted[q] || lifted[w] || lifted[r]) && xs[r] !== xs[w]) idx.push(base + q, base + r, base + w);
      }
    }
  };
  for (const s of list) strip(s);
  if (!idx.length) return null;
  // only the vertices a triangle uses (a strip's corners its record does not lift, folded or beyond the foot, are left out)
  const remap = new Int32Array(pos.length / 3).fill(-1);
  let used = 0;
  for (const v of idx) if (remap[v] < 0) remap[v] = used++;
  const positions = new Float32Array(3 * used), colors = new Float32Array(3 * used);
  for (let v = 0; v < remap.length; v++) {
    const u = remap[v];
    if (u < 0) continue;
    positions[3 * u] = pos[3 * v]; positions[3 * u + 1] = pos[3 * v + 1]; positions[3 * u + 2] = pos[3 * v + 2];
    colors[3 * u] = col[3 * v]; colors[3 * u + 1] = col[3 * v + 1]; colors[3 * u + 2] = col[3 * v + 2];
  }
  return { positions, colors, index: Uint32Array.from(idx, v => remap[v]), triangles: idx.length / 3 };
}

/** The strips as a mesh for the planet's mesh to hold (its origin the planet's centre): the material's lift is the caller's. */
export function landformFarMesh(planet: PlanetState, meshColors?: Float32Array, person = HUMAN_PERSON): THREE.Mesh | null {
  const s = landformStrips(planet, person, meshColors);
  if (!s) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(s.positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(s.colors, 3));
  geo.setIndex(new THREE.BufferAttribute(s.index, 1));
  geo.computeVertexNormals();
  const mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true }));
  mesh.name = 'landformFar';
  return mesh;
}
